import { useMemo } from 'react'

import { compact } from 'lodash'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'

import { ErrorMessages } from '~/components/FormErrorMessages'
import {
  REFRESH_QUOTE_INTERVAL,
  useGetSwapLoggingData,
} from '~/components/forms/swap/hooks/useSwap'
import { TransferFormSection } from '~/components/forms/transfer/TransferFormSection'
import { useAddTransferToBatch } from '~/components/forms/transfer/hooks/useAddTransferToBatch'
import { useOfframpDisplayData } from '~/components/forms/transfer/hooks/useOfframpDisplayData'
import { useStateChangesToHide } from '~/components/forms/transfer/hooks/useStateChangesToHide'
import {
  useIsInsufficientFunds,
  useCreateTransferTransactionSubmit,
  useTransferForm,
} from '~/components/forms/transfer/hooks/useTransferForm'
import { useTransferQuote } from '~/components/forms/transfer/hooks/useTransferQuote'
import {
  transferSchema,
  TransferFormValues,
} from '~/components/forms/transfer/transferSchema'
import { TransactionDialogContent } from '~/components/smart-accounts/transactions/TransactionDialogContent'
import { createTransactionHandlers } from '~/components/smart-accounts/transactions/createTransactionHandlers'
import { useCompliantPaymentValidation } from '~/components/smart-accounts/useCompliantPaymentValidation'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrgId } from '~/hooks/org'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { buildAddToBatchDropdownOptions } from '~/hooks/useAddCurrentFormToBatch'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress, TransactionRecipient } from '~/types'
import { Address } from '~/utils/address'
import { getSwapTransferBridgeStateChanges } from '~/utils/smartAccounts/transactions'

const DialogFormTransaction = ({
  defaultSender,
  defaultRecipient,
  isOfframpVariant,
  customTitle,
  lockRecipient,
  setIsOpen,
}: {
  defaultSender?: IAddress
  defaultRecipient?: IAddress
  isOfframpVariant?: boolean
  customTitle?: string
  lockRecipient?: boolean
  setIsOpen: (open: boolean) => void
}) => {
  const methods = useFormContext<TransferFormValues>()

  const activeOrgId = useActiveOrgId()

  const { control, getValues } = methods

  const sender = useWatch({ control, name: 'sender' })
  const recipients = useWatch({ control, name: 'recipients' })
  const swapData = useWatch({ control, name: 'swapData' })
  const transferToken = useWatch({ control, name: 'transferToken' })

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })

  const sellTokenMetadata = useTokenMetadata({
    address: swapData?.sellToken?.token,
    chainId: swapData?.sellToken?.chainId,
  })

  const receiver = recipients[0]?.receiver?.address

  const offrampData = useOfframpDisplayData({
    enabled: !!isOfframpVariant,
    receiver,
    transferToken,
    transferTokenMetadata,
  })

  const { getLoggingData } = useGetSwapLoggingData({
    sellAmount: swapData?.sellToken?.amount ?? '0',
    sellToken: swapData?.sellToken?.token as IAddress,
    sellChainId: swapData?.sellToken?.chainId,
    buyToken: transferToken?.token as IAddress,
    buyChainId: transferToken?.chainId,
    buyAmount: transferToken?.amount ?? '0',
    sender,
    recipients: useMemo(
      () =>
        recipients?.map((recipient) => ({
          address: recipient.receiver?.address,
        })),
      [recipients],
    ),
    orgId: activeOrgId,
    slippage: swapData?.slippage,
  })

  const isInsufficientFunds = useIsInsufficientFunds({
    sender,
    token: transferToken?.token,
    chainId: transferToken?.chainId,
    amount: transferToken?.amount,
  })

  const { smartAccounts } = useSmartAccounts()

  const senderAccount = smartAccounts.find(
    (account) => account.address === sender,
  )

  const { isLoading: isLoadingQuote, dataUpdatedAt } = useTransferQuote()

  const isValidForm =
    Object.keys(methods.formState.errors ?? {}).length === 0 &&
    transferSchema.safeParse(getValues()).success &&
    (isInsufficientFunds ? (swapData ? true : false) : true) &&
    !isLoadingQuote

  const { createTransactionOnSubmit, isProcessingTransaction } =
    useCreateTransferTransactionSubmit({
      activeOrgId,
      transferTokenChainId: transferToken?.chainId,
      transferTokenAddress: transferToken?.token,
      sellTokenMetadata,
      transferTokenMetadata,
      setIsOpen,
    })

  const {
    approveAndSubmitTransaction,
    isProcessing: isProcessingApproveAndSubmit,
  } = useApproveAndSubmitTransaction()

  const isLoading = isProcessingApproveAndSubmit || isProcessingTransaction

  const transactionHandlers = createTransactionHandlers<TransferFormValues>({
    methods,
    senderAccount: senderAccount!,
    getLoggingData: (data) =>
      getLoggingData({
        routerType: data.swapData?.swapRouter?.toString(),
      }),
    createTransactionOnSubmit,
    approveAndSubmitTransaction,
  })

  useCompliantPaymentValidation()

  const dialogTitle = customTitle
    ? customTitle
    : `${isOfframpVariant ? 'Offramp' : 'Send'}${
        senderAccount && defaultSender ? ` from ${senderAccount.name}` : ''
      }`

  const {
    handleAddToBatch,
    isProcessing: isAddingToBatch,
    isMetadataReady: isBatchMetadataReady,
    transactionChainId: chainId,
    transactionTypes,
    canAddToBatch,
  } = useAddTransferToBatch({
    senderAccount,
    title: dialogTitle,
    setIsOpen,
    isOfframp: !!isOfframpVariant,
  })

  const bridgeStateChanges = useMemo(
    () =>
      getSwapTransferBridgeStateChanges({
        transferToken,
        transactionChainId: chainId,
        transferTokenMetadata,
        recipients: recipients?.map((recipient) => ({
          ...recipient,
          receiver: {
            address: Address.safeFormat(recipient.receiver?.address),
          },
        })),
      }),
    [transferToken, chainId, transferTokenMetadata, recipients],
  )

  const stateChangesToHide = useStateChangesToHide()

  return (
    <TransactionDialogContent
      id="sendDetails"
      title={dialogTitle}
      isValidForm={isValidForm}
      isLoading={isLoading}
      handlers={transactionHandlers}
      chainId={chainId}
      smartAccountAddress={sender}
      addressesToWatch={useMemo(
        () => [
          sender,
          ...compact(
            recipients.map((recipient) => recipient.receiver?.address),
          ),
        ],
        [sender, recipients],
      )}
      bridgeStateChanges={bridgeStateChanges}
      // When the sell token is a position there is a small amount of the underlying asset in the account after the swap.
      // We don't want to show this in the state change list.
      stateChangesToHide={stateChangesToHide}
      transactionTypes={transactionTypes}
      // Kept as "SwapTransfer" for analytics continuity, even after the
      // internal SwapTransfer → Transfer rename (see dc193ba5e).
      eventType="SwapTransfer"
      offrampData={offrampData}
      refreshData={
        dataUpdatedAt
          ? {
              dataUpdatedAt,
              refetchInterval: REFRESH_QUOTE_INTERVAL,
              isLoading: isLoadingQuote,
            }
          : undefined
      }
      reviewDropdownOptions={buildAddToBatchDropdownOptions({
        canAddToBatch,
        isDisabled: !isValidForm || !isBatchMetadataReady || isAddingToBatch,
        onClick: handleAddToBatch,
      })}
      afterMemo={<ErrorMessages errors={methods.formState.errors} />}
    >
      <TransferFormSection
        defaultSender={defaultSender}
        defaultRecipient={defaultRecipient}
        isOfframpVariant={isOfframpVariant}
        lockRecipients={lockRecipient}
        isLoadingQuote={isLoadingQuote}
      />
    </TransactionDialogContent>
  )
}

export const DialogFormContent = ({
  defaultSender,
  defaultRecipient,
  isOfframpVariant,
  customTitle,
  lockRecipient = false,
  setIsOpen,
  defaultRecipients,
  defaultToken,
  defaultMemo,
  transactionId,
}: {
  defaultSender?: IAddress
  defaultRecipient?: IAddress
  isOfframpVariant?: boolean
  customTitle?: string
  lockRecipient?: boolean
  defaultRecipients?: TransactionRecipient[]
  defaultToken?: {
    token?: IAddress
    chainId?: SupportedChainId
    amount?: string
  }
  setIsOpen: (open: boolean) => void
  transactionId?: string
  defaultMemo?: string
}) => {
  const methods = useTransferForm({
    defaultSender,
    defaultRecipients,
    defaultTransferToken: defaultToken,
    defaultMemo,
    transactionId,
  })

  return (
    <FormProvider {...methods}>
      <DialogFormTransaction
        defaultSender={defaultSender}
        defaultRecipient={defaultRecipient}
        isOfframpVariant={isOfframpVariant}
        customTitle={customTitle}
        lockRecipient={lockRecipient}
        setIsOpen={setIsOpen}
      />
    </FormProvider>
  )
}
